// CurrencyRatesPanel — read-only rates table + "Refresh from FX provider" button.
//
// Rendered inside SchemaConfigEditor when namespace === 'currency-settings'.
// Reads displayCurrencies from the config blob and lets operators trigger an
// on-demand FX pull. Large moves (>25%) are held back until the operator
// explicitly confirms them with "Apply anyway".

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAdminConfig,
  refreshFxRates,
  type FxRefreshResponse,
} from '../lib/admin-api';

interface Entry {
  code: string;
  symbol: string;
  rateToPrimary?: number;
}

export function CurrencyRatesPanel() {
  const qc = useQueryClient();
  const [result, setResult] = useState<FxRefreshResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cfg = useQuery({
    queryKey: ['admin', 'configs', 'currency-settings'],
    queryFn: ({ signal }) => fetchAdminConfig('currency-settings', signal),
  });

  const entries: Entry[] =
    (cfg.data?.value as { displayCurrencies?: Entry[] } | undefined)
      ?.displayCurrencies ?? [];

  const refresh = useMutation({
    mutationFn: (confirm: boolean) => refreshFxRates(confirm),
    onSuccess: (res) => {
      setResult(res);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'configs', 'currency-settings'] });
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : 'Refresh failed');
    },
  });

  const hasFlagged = (result?.flagged?.length ?? 0) > 0;

  return (
    <section className="mt-6 rounded-xl border border-[color:var(--bo-line)] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--bo-line)] px-5 py-3">
        <div>
          <div className="text-[13px] font-semibold text-[color:var(--bo-ink)]">
            Display currencies &amp; rates
          </div>
          <div className="mt-0.5 text-[11.5px] text-[color:var(--bo-mute)]">
            Live exchange rates relative to the primary currency. Read-only — updated via
            the FX provider button.
          </div>
        </div>
        <button
          type="button"
          disabled={refresh.isPending}
          onClick={() => refresh.mutate(false)}
          className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {refresh.isPending ? 'Refreshing…' : 'Refresh from FX provider'}
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="px-5 py-4 text-[12.5px] text-[color:var(--bo-mute)]">
          {cfg.isLoading
            ? 'Loading currencies…'
            : 'No display currencies configured yet. Add them in the form above and save first.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[12.5px]">
            <thead>
              <tr className="border-b border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]">
                <th className="px-5 py-2 font-medium uppercase tracking-wide text-[11px] text-[color:var(--bo-mute)]">
                  Code
                </th>
                <th className="px-5 py-2 font-medium uppercase tracking-wide text-[11px] text-[color:var(--bo-mute)]">
                  Symbol
                </th>
                <th className="px-5 py-2 font-medium uppercase tracking-wide text-[11px] text-[color:var(--bo-mute)]">
                  Rate to primary
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--bo-line)]">
              {entries.map((e) => (
                <tr key={e.code} className="hover:bg-[color:var(--bo-bg)]">
                  <td className="px-5 py-2 font-mono text-[color:var(--bo-ink)]">{e.code}</td>
                  <td className="px-5 py-2 text-[color:var(--bo-ink)]">{e.symbol}</td>
                  <td className="px-5 py-2 font-mono text-[color:var(--bo-ink)]">
                    {e.rateToPrimary ?? 1}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Error banner */}
      {error ? (
        <div className="border-t border-[color:var(--bo-line)] bg-[#fff1f2] px-5 py-2 text-[12.5px] text-[#9f1239]">
          {error}
        </div>
      ) : null}

      {/* Result summary */}
      {result ? (
        <div className="space-y-2 border-t border-[color:var(--bo-line)] px-5 py-3 text-[12.5px]">
          <p className="text-[color:var(--bo-mute)]">
            Provider time:{' '}
            <span className="font-mono text-[color:var(--bo-ink)]">
              {result.fetchedAt ?? 'n/a'}
            </span>
          </p>
          {result.applied.length > 0 ? (
            <p className="text-[#166534]">
              Updated:{' '}
              {result.applied
                .map((a) => `${a.code} ${a.from != null ? a.from : '–'}→${a.to}`)
                .join(', ')}
            </p>
          ) : null}
          {result.applied.length === 0 && !hasFlagged ? (
            <p className="text-[color:var(--bo-mute)]">All rates are already up to date.</p>
          ) : null}
          {result.skipped.length > 0 ? (
            <p className="text-[color:var(--bo-mute)]">
              Skipped (no FX data): {result.skipped.join(', ')}
            </p>
          ) : null}
          {hasFlagged ? (
            <div className="rounded-lg border border-[#fde68a] bg-[#fff7ed] px-4 py-3">
              <p className="text-[12.5px] text-[#9a3412]">
                Large moves held back (&gt;25%):{' '}
                {result.flagged
                  .map((f) => `${f.code} ${f.from}→${f.to} (${f.pct.toFixed(1)}%)`)
                  .join(', ')}
              </p>
              <button
                type="button"
                onClick={() => refresh.mutate(true)}
                disabled={refresh.isPending}
                className="mt-2 inline-flex h-8 items-center rounded-lg bg-[#9a3412] px-3 text-[12px] font-semibold text-white transition hover:bg-[#7c2d12] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {refresh.isPending ? 'Applying…' : 'Apply anyway'}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
